import { ElementRef, AfterViewInit, Directive, Renderer2, ViewContainerRef, Input, inject } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatButton } from "@angular/material/button";

interface PageObject {
  length: number;
  pageIndex: number;
  pageSize: number;
  previousPageIndex: number;
}

@Directive({
    selector: "[AppStylePaginator]",
    standalone: false
})
export class StylePaginatorDirective {
  private readonly matPag = inject(MatPaginator, { host: true, self: true, optional: true });
  private vr = inject(ViewContainerRef);
  private ren = inject(Renderer2);

  private _pageGapTxt = "...";
  private _rangeStart: number;
  private _rangeEnd: number;
  private _buttons = [];
  private _curPageObj: PageObject = {
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  };

  @Input()
  get showTotalPages(): number {
    return this._showTotalPages;
  }
  set showTotalPages(value: number) {
    this._showTotalPages = value % 2 == 0 ? value + 1 : value;
  }
  private _showTotalPages = 2;

  get inc(): number {
    return this._showTotalPages % 2 == 0
      ? this.showTotalPages / 2
      : (this.showTotalPages - 1) / 2;
  }

  get numOfPages(): number {
    return this.matPag.getNumberOfPages();
  }

  get lastPageIndex(): number {
    return this.matPag.getNumberOfPages() - 1;
  }

  constructor() {
    //to rerender buttons on items per page change and first, last, next and prior buttons
    this.matPag.page.subscribe((e: PageObject) => {
      if (
        this._curPageObj.pageSize != e.pageSize &&
        this._curPageObj.pageIndex != 0
      ) {
        e.pageIndex = 0;
        this._rangeStart = 0;
        this._rangeEnd = this._showTotalPages - 1;
      }
      this._curPageObj = e;

      this.initPageRange();
    });
  }

  private buildPageNumbers() {
    const actionContainer = this.vr.element.nativeElement.querySelector(
      "div.mat-mdc-paginator-range-actions"
    );
    const nextPageNode = this.vr.element.nativeElement.querySelector(
      "button.mat-mdc-paginator-navigation-next"
    );
    const prevButtonCount = this._buttons.length;

    // remove buttons before creating new ones
    if (this._buttons.length > 0) {
      this._buttons.forEach((button) => {
        this.ren.removeChild(actionContainer, button);
      });
      //Empty state array
      this._buttons.length = 0;
    }

    //initialize next page and last page buttons
    if (this._buttons.length == 0) {
      let nodeArray =
        this.vr.element.nativeElement.childNodes[0].childNodes[0].childNodes[2]
          .childNodes;
      setTimeout(() => {
        for (let i = 0; i < nodeArray.length; i++) {
          if (nodeArray[i].nodeName === "BUTTON") {
            if (nodeArray[i].innerHTML.length > 100 && nodeArray[i].disabled) {
            } else if (
              nodeArray[i].innerHTML.length > 100 &&
              !nodeArray[i].disabled
            ) {
            } else if (nodeArray[i].disabled) {
              this.ren.addClass(nodeArray[i], "mat-raised-button");
              this.ren.addClass(nodeArray[i], "mat-primary");
            }
          }
        }
      });
    }

    for (let i = 0; i < this.numOfPages; i++) {
      if (i >= this._rangeStart && i <= this._rangeEnd) {
        this.ren.insertBefore(
          actionContainer,
          this.createButton(i, this.matPag.pageIndex),
          nextPageNode
        );
      }

    //   if (i == this._rangeEnd) {
    //     this.ren.insertBefore(
    //       actionContainer,
    //       this.createButton(this._pageGapTxt, this._rangeEnd),
    //       nextPageNode
    //     );
    //   }
    }
  }

  /**
   * Establece el estilo del boton seleccionado
   * @param linkBtn boton
   */
  setStyleButtonSelected( linkBtn: MatButton ) {
    this.ren.setStyle(linkBtn, "background", "#294ADB");
    this.ren.setStyle(linkBtn, "color", "white");
  }

  private createButton(i: any, pageIndex: number): any   {
    const linkBtn: MatButton = this.ren.createElement("button");

    [
      "mat-mdc-button-base", "mat-mdc-icon-button", "mat-primary", "mdc-icon-button"
    ].forEach(c => this.ren.addClass(linkBtn, c));

    this.ren.setAttribute(linkBtn, "mat-ripple-loader-class-name", "mat-mdc-button-ripple")
    
    this.ren.setStyle(linkBtn, "margin", "1%");
    this.ren.setStyle(linkBtn, "margin-radius", "0%");
    this.ren.setStyle(linkBtn, "border-radius", "50%");
    this.ren.setStyle(linkBtn, "font-size", "14px");

    const pagingTxt = isNaN(i) ? this._pageGapTxt : +(i + 1);
    const text = this.ren.createText(pagingTxt + "");

    this.ren.addClass(linkBtn, "mat-mdc-custom-page");
    
    switch (i) {
      case pageIndex:
        this.ren.setAttribute(linkBtn, "disabled", "disabled");
        this.setStyleButtonSelected(linkBtn);
        break;
      case this._pageGapTxt:
        let newIndex = this._curPageObj.pageIndex + this._showTotalPages;

        if (newIndex >= this.numOfPages) newIndex = this.lastPageIndex;

        if (pageIndex != this.lastPageIndex) {
          this.ren.listen(linkBtn, "click", () => {
            this.switchPage(newIndex);
          });
        }

        if (pageIndex == this.lastPageIndex) {
          this.ren.setAttribute(linkBtn, "disabled", "disabled");
        }
        break;
      default:
        this.ren.listen(linkBtn, "click", () => {
          this.setStyleButtonSelected(linkBtn);
          this.switchPage(i);
        });
        break;
    }

    const indicator: HTMLElement = this.ren.createElement("span");
    const persisten: HTMLElement = this.ren.createElement("span");
    const target: HTMLElement = this.ren.createElement("span");
    const ripple: HTMLElement = this.ren.createElement("span");
    const buttonLabel: HTMLElement = this.ren.createElement("span");

    this.ren.addClass(buttonLabel, "mdc-button__label");

    this.ren.addClass(indicator, "mat-mdc-focus-indicator");
    this.ren.addClass(persisten, "mat-mdc-button-touch-target");
    
    this.ren.addClass(ripple, "mat-mdc-button-ripple");
    this.ren.addClass(ripple, "mat-ripple");

    this.ren.addClass(target, "mat-mdc-button-persistent-ripple");
    this.ren.addClass(target, "mdc-icon-button__ripple");

    this.ren.appendChild(buttonLabel, text);

    this.ren.appendChild(linkBtn, buttonLabel);
    this.ren.appendChild(linkBtn, indicator);
    this.ren.appendChild(linkBtn, target);
    this.ren.appendChild(linkBtn, persisten);
    this.ren.appendChild(linkBtn, ripple);

    //Add button to private array for state
    this._buttons.push(linkBtn);
    return linkBtn;
  }
  //calculates the button range based on class input parameters and based on current page index value. Used to render new buttons after event.
  private initPageRange(): void {
    const middleIndex = (this._rangeStart + this._rangeEnd) / 2;

    this._rangeStart = this.calcRangeStart(middleIndex);
    this._rangeEnd = this.calcRangeEnd(middleIndex);

    this.buildPageNumbers();
  }

  //Helper function To calculate start of button range
  private calcRangeStart(middleIndex: number): number {
    switch (true) {
      case this._curPageObj.pageIndex == 0 && this._rangeStart != 0:
        return 0;
      case this._curPageObj.pageIndex > this._rangeEnd:
        return this._curPageObj.pageIndex + this.inc > this.lastPageIndex
          ? this.lastPageIndex - this.inc * 2
          : this._curPageObj.pageIndex - this.inc;
      case this._curPageObj.pageIndex > this._curPageObj.previousPageIndex &&
        this._curPageObj.pageIndex > middleIndex &&
        this._rangeEnd < this.lastPageIndex:
        return this._rangeStart + 1;
      case this._curPageObj.pageIndex < this._curPageObj.previousPageIndex &&
        this._curPageObj.pageIndex < middleIndex &&
        this._rangeStart > 0:
        return this._rangeStart - 1;
      default:
        return this._rangeStart;
    }
  }
  //Helpter function to calculate end of button range
  private calcRangeEnd(middleIndex: number): number {
    switch (true) {
      case this._curPageObj.pageIndex == 0 &&
        this._rangeEnd != this._showTotalPages:
        return this._showTotalPages - 1;
      case this._curPageObj.pageIndex > this._rangeEnd:
        return this._curPageObj.pageIndex + this.inc > this.lastPageIndex
          ? this.lastPageIndex
          : this._curPageObj.pageIndex + 1;
      case this._curPageObj.pageIndex > this._curPageObj.previousPageIndex &&
        this._curPageObj.pageIndex > middleIndex &&
        this._rangeEnd < this.lastPageIndex:
        return this._rangeEnd + 1;
      case this._curPageObj.pageIndex < this._curPageObj.previousPageIndex &&
        this._curPageObj.pageIndex < middleIndex &&
        this._rangeStart >= 0 &&
        this._rangeEnd > this._showTotalPages - 1:
        return this._rangeEnd - 1;
      default:
        return this._rangeEnd;
    }
  }
  //Helper function to switch page on non first, last, next and previous buttons only.
  private switchPage(i: number): void {
    const previousPageIndex = this.matPag.pageIndex;
    this.matPag.pageIndex = i;
    this.matPag["_emitPageEvent"](previousPageIndex);
    this.initPageRange();
  }
  //Initialize default state after view init
  public ngAfterViewInit() {
    this._rangeStart = 0;
    this._rangeEnd = this._showTotalPages - 1;
    this.initPageRange();
  }
}